'use client';
import Link from 'next/link';
import { Box, Card, CardContent, Theme } from '@mui/material';

import { ChanceIndicator, MarketCardFooter, MarketCardHeader, TradeButtons } from '@/components';
import { useBookmarkToggle, useIsBookmarked } from '@/hooks';
import type { MarketIcon } from '@/types';

import { ROUTES } from '@/constants';

export interface QuestionCardProps {
  id: string;
  question: string;
  icon?: MarketIcon;
  iconImage?: React.ReactNode;
  image?: string;
  chance: string;
  volume: number;
  onTradeClick?: (cardId: string, isYes: boolean) => void;
  showRefreshIcon?: boolean;
  showGiftIcon?: boolean;
  isLive?: boolean;
}

export function QuestionCard({
  id,
  question,
  image,
  chance,
  volume,
  onTradeClick,
  isLive,
}: QuestionCardProps) {
  const isBookmarked = useIsBookmarked(id);
  const handleBookmarkToggle = useBookmarkToggle();

  function handleYesClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onTradeClick?.(id, true);
  }

  function handleNoClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onTradeClick?.(id, false);
  }

  function handleBookmarkClick(event: React.MouseEvent<HTMLButtonElement>) {
    handleBookmarkToggle(event, { id } as any, 'market');
  }

  return (
    <Link href={ROUTES.MARKET_DETAILS(id)} style={{ textDecoration: 'none' }}>
      <Card
        sx={{
          height: 240,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: (theme: Theme) =>
              theme.palette.mode === 'dark'
                ? '0 4px 12px rgba(0,0,0,0.4)'
                : '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            p: 1.5,
            overflow: 'hidden',
            '&:last-child': { pb: 1.5 },
          }}
        >
          {/* Content Area */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 0, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <MarketCardHeader
                title={question}
                imageUrl={image}
                action={<ChanceIndicator percentage={chance} />}
              />
            </Box>

            {/* Middle: Yes/No Buttons - Centered Row */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <TradeButtons onYesClick={handleYesClick} onNoClick={handleNoClick} />
            </Box>
            <Box sx={{ p: 0, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}></Box>
          </Box>

          {/* Footer - Volume and Icons */}
          <MarketCardFooter
            volume={volume}
            isBookmarked={isBookmarked}
            onBookmarkClick={handleBookmarkClick}
            isLive={isLive}
          />
        </CardContent>
      </Card>
    </Link>
  );
}

export default QuestionCard;
